/**
 * ConsolidationComptableV2 - Multi-tenant accounting consolidation
 * 
 * This is the V2 version that:
 * - Uses Rachel v2 API endpoints (/api/rachel-v2/*)
 * - Is fully tenant-scoped
 * - Does NOT modify v1 collections
 * - Stores decisions in rachel_v2_* collections
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Brain, 
  TrendingUp, 
  AlertCircle, 
  CheckCircle, 
  XCircle,
  RefreshCw,
  Search,
  Filter,
  Download,
  ChevronDown,
  ChevronRight,
  Building2,
  Sparkles,
  BookOpen,
  History,
  Settings,
  Users,
  UserCheck,
  MapPin,
  ThumbsUp,
  PlayCircle,
  Eye,
  Zap,
  ExternalLink
} from 'lucide-react';
import { apiGet, apiPost, getCurrentTenant, initializeTenant } from '../utils/apiFetch';
import PennylaneLinkModal from './PennylaneLinkModal';
import getApiUrl from '../utils/getApiUrl';

const API_URL = getApiUrl();

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function ConsolidationComptableV2() {
  // Tenant state
  const [tenant, setTenant] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  
  // Data state
  const [transactions, setTransactions] = useState([]);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [learnings, setLearnings] = useState([]);
  const [decisions, setDecisions] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('transactions');
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [classifyingId, setClassifyingId] = useState(null);
  
  // Batch modal state
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchResults, setBatchResults] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  
  // Attribution modal state
  const [showAttributionModal, setShowAttributionModal] = useState(false);
  const [attributionTx, setAttributionTx] = useState(null);
  const [clientMatches, setClientMatches] = useState([]);
  const [attributionLoading, setAttributionLoading] = useState(false);
  const [attributions, setAttributions] = useState([]);

  // Batch Attribution modal state
  const [showBatchAttributionModal, setShowBatchAttributionModal] = useState(false);
  const [batchAttributionResults, setBatchAttributionResults] = useState(null);
  const [batchAttributionLoading, setBatchAttributionLoading] = useState(false);
  const [batchConfig, setBatchConfig] = useState(null);

  // Pennylane Link modal state
  const [showPennylaneLinkModal, setShowPennylaneLinkModal] = useState(false);
  const [pennylaneLinkTransaction, setPennylaneLinkTransaction] = useState(null);

  // ═══════════════════════════════════════════════════════════════
  // INITIALIZATION
  // ═══════════════════════════════════════════════════════════════

  useEffect(() => {
    async function init() {
      setTenantLoading(true);
      try {
        const t = await initializeTenant();
        setTenant(t);
        if (t) {
          await loadData();
        }
      } catch (err) {
        setError('Erreur initialisation tenant: ' + err.message);
      } finally {
        setTenantLoading(false);
      }
    }
    init();
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Load all data in parallel
      const [configRes, statsRes, learningsRes, decisionsRes, txRes, attrRes, batchConfRes] = await Promise.all([
        apiGet('/api/rachel-v2/config'),
        apiGet('/api/rachel-v2/stats'),
        apiGet('/api/rachel-v2/learnings'),
        apiGet('/api/rachel-v2/decisions?limit=100'),
        apiGet('/api/rachel-v2/import/cb-transactions?limit=100'),
        apiGet('/api/rachel-v2/attributions?limit=100'),
        apiGet('/api/rachel-v2/batch-attribute/config')
      ]);
      
      if (configRes.success) setConfig(configRes.config);
      if (statsRes.success) setStats(statsRes);
      if (learningsRes.success) setLearnings(learningsRes.learnings);
      if (decisionsRes.success) setDecisions(decisionsRes.decisions);
      if (txRes.success) setTransactions(txRes.transactions);
      if (attrRes.success) setAttributions(attrRes.attributions || []);
      if (batchConfRes.success) setBatchConfig(batchConfRes.batch_attribution);
      
    } catch (err) {
      setError('Erreur chargement données: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // ACTIONS
  // ═══════════════════════════════════════════════════════════════

  const classifyTransaction = async (tx) => {
    setClassifyingId(tx.id);
    try {
      const result = await apiPost('/api/rachel-v2/classify', {
        label: tx.label || tx.full_line || '',
        supplier: tx.supplier || tx.supplier_detected,
        amount: tx.amount,
        use_ai: true
      });
      
      if (result.success) {
        // Update local state with classification
        setTransactions(prev => prev.map(t => 
          t.id === tx.id ? { ...t, _v2_classification: result } : t
        ));
        
        // Reload stats
        const statsRes = await apiGet('/api/rachel-v2/stats');
        if (statsRes.success) setStats(statsRes);
      }
    } catch (err) {
      setError('Erreur classification: ' + err.message);
    } finally {
      setClassifyingId(null);
    }
  };

  const runBatchClassify = async () => {
    setBatchLoading(true);
    setBatchResults(null);
    try {
      // Prepare transactions for batch
      const txToClassify = selectedTransactions.length > 0
        ? transactions.filter(t => selectedTransactions.includes(t.id))
        : transactions.slice(0, 50);
      
      const payload = {
        transactions: txToClassify.map(t => ({
          id: t.id,
          label: t.label || t.full_line || '',
          supplier: t.supplier || t.supplier_detected,
          amount: t.amount,
          source: 'cb'
        })),
        use_ai: true
      };
      
      const result = await apiPost('/api/rachel-v2/batch-classify', payload);
      
      if (result.success) {
        setBatchResults(result);
        // Reload data
        await loadData();
      }
    } catch (err) {
      setError('Erreur batch: ' + err.message);
    } finally {
      setBatchLoading(false);
    }
  };

  // Attribution functions
  const findClientForTransaction = async (tx) => {
    setAttributionTx(tx);
    setShowAttributionModal(true);
    setAttributionLoading(true);
    setClientMatches([]);
    
    try {
      const result = await apiPost('/api/rachel-v2/find-client', {
        supplier: tx.supplier || tx.supplier_detected,
        label: tx.label || tx.full_line || '',
        amount: tx.amount,
        date: tx.date,
        transaction_id: tx.id
      });
      
      if (result.success) {
        setClientMatches(result.matches || []);
      } else {
        setError(result.error || 'Erreur recherche client');
      }
    } catch (err) {
      setError('Erreur recherche client: ' + err.message);
    } finally {
      setAttributionLoading(false);
    }
  };

  const confirmAttribution = async (match) => {
    if (!attributionTx || !match) return;
    
    setAttributionLoading(true);
    try {
      const result = await apiPost('/api/rachel-v2/attribute', {
        source: 'cb',
        source_id: attributionTx.id,
        client_id: String(match.client.client_id),
        client_name: match.client.nom,
        probability: match.probability,
        reasons: match.reasons,
        status: 'confirmed'
      });
      
      if (result.success) {
        // Update local state
        setTransactions(prev => prev.map(t =>
          t.id === attributionTx.id 
            ? { ...t, _v2_attribution: { status: 'confirmed', client: match.client, probability: match.probability } }
            : t
        ));
        setShowAttributionModal(false);
        setAttributionTx(null);
        // Reload attributions
        const attrRes = await apiGet('/api/rachel-v2/attributions?limit=100');
        if (attrRes.success) setAttributions(attrRes.attributions || []);
      } else {
        setError(result.error || 'Erreur attribution');
      }
    } catch (err) {
      setError('Erreur attribution: ' + err.message);
    } finally {
      setAttributionLoading(false);
    }
  };

  // Get attribution status for a transaction
  const getAttributionStatus = (txId) => {
    return attributions.find(a => a.source_id === txId);
  };

  // Batch Attribution functions
  const runBatchAttribution = async (dryRun = true) => {
    setBatchAttributionLoading(true);
    setBatchAttributionResults(null);
    
    try {
      const txIds = selectedTransactions.length > 0 
        ? selectedTransactions 
        : null; // null = all unattributed
      
      const result = await apiPost('/api/rachel-v2/batch-attribute', {
        transaction_ids: txIds,
        dry_run: dryRun
      });
      
      if (result.success) {
        setBatchAttributionResults(result);
        if (!dryRun) {
          // Reload attributions after actual run
          const attrRes = await apiGet('/api/rachel-v2/attributions?limit=100');
          if (attrRes.success) setAttributions(attrRes.attributions || []);
        }
      } else {
        setError(result.error || 'Erreur batch attribution');
      }
    } catch (err) {
      setError('Erreur batch: ' + err.message);
    } finally {
      setBatchAttributionLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER HELPERS
  // ═══════════════════════════════════════════════════════════════

  const getClassificationBadge = (classification) => {
    if (!classification) return null;
    
    const cls = classification.classification || classification;
    const source = classification.source;
    
    const isAttributable = cls.est_attribuable_chantier;
    const type = cls.type_tiers || 'unknown';
    
    let bgColor = 'bg-gray-100 text-gray-700';
    let icon = <AlertCircle className="w-3 h-3" />;
    
    if (source === 'learned') {
      bgColor = 'bg-green-100 text-green-700';
      icon = <CheckCircle className="w-3 h-3" />;
    } else if (source === 'ai') {
      bgColor = 'bg-orange-100 text-orange-700';
      icon = <Brain className="w-3 h-3" />;
    } else if (source?.includes('business_rules')) {
      bgColor = 'bg-blue-100 text-blue-700';
      icon = <BookOpen className="w-3 h-3" />;
    }
    
    if (isAttributable) {
      bgColor = 'bg-purple-100 text-purple-700';
    } else if (type === 'frais_generaux') {
      bgColor = 'bg-gray-100 text-gray-600';
    }
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${bgColor}`}>
        {icon}
        {cls.categorie || type}
      </span>
    );
  };

  const formatAmount = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  // ═══════════════════════════════════════════════════════════════
  // LOADING STATE
  // ═══════════════════════════════════════════════════════════════

  if (tenantLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-2" />
          <p className="text-gray-600">Chargement du contexte tenant...</p>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-red-600">
          <AlertCircle className="w-8 h-8 mx-auto mb-2" />
          <p>Aucun tenant configuré. Contactez l'administrateur.</p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Consolidation Comptable v2
                </h1>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  {tenant.name} • {tenant.industry_pack?.toUpperCase()}
                </p>
              </div>
              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded">
                TEST
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={loadData}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Actualiser
              </button>
              <button
                onClick={() => setShowBatchAttributionModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                data-testid="batch-attribution-btn"
              >
                <Users className="w-4 h-4" />
                Attribution Batch
              </button>
              <button
                onClick={() => setShowBatchModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
              >
                <Sparkles className="w-4 h-4" />
                Analyse Masse
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 mt-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-800 font-medium">Erreur</p>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            icon={<TrendingUp className="w-5 h-5" />}
            label="Transactions"
            value={transactions.length}
            color="blue"
          />
          <StatCard
            icon={<Brain className="w-5 h-5" />}
            label="Fournisseurs appris"
            value={stats?.knowledge?.suppliers_known || 0}
            color="green"
          />
          <StatCard
            icon={<History className="w-5 h-5" />}
            label="Décisions v2"
            value={decisions.length}
            color="purple"
          />
          <StatCard
            icon={<Settings className="w-5 h-5" />}
            label="Mots-clés config"
            value={(config?.general_charges_keywords?.length || 0) + (config?.client_attributable_keywords?.length || 0)}
            color="orange"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4">
        <div className="border-b border-gray-200">
          <nav className="flex gap-4">
            {[
              { id: 'transactions', label: 'Transactions', icon: <TrendingUp className="w-4 h-4" /> },
              { id: 'learnings', label: 'Apprentissages', icon: <Brain className="w-4 h-4" /> },
              { id: 'decisions', label: 'Décisions', icon: <History className="w-4 h-4" /> },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === 'transactions' && (
          <TransactionsTab
            transactions={transactions}
            onClassify={classifyTransaction}
            classifyingId={classifyingId}
            selectedTransactions={selectedTransactions}
            setSelectedTransactions={setSelectedTransactions}
            getClassificationBadge={getClassificationBadge}
            formatAmount={formatAmount}
            onFindClient={findClientForTransaction}
            getAttributionStatus={getAttributionStatus}
            onPennylaneLink={(tx) => {
              setPennylaneLinkTransaction(tx);
              setShowPennylaneLinkModal(true);
            }}
          />
        )}
        
        {activeTab === 'learnings' && (
          <LearningsTab
            learnings={learnings}
            formatAmount={formatAmount}
          />
        )}
        
        {activeTab === 'decisions' && (
          <DecisionsTab
            decisions={decisions}
            getClassificationBadge={getClassificationBadge}
          />
        )}
      </div>

      {/* Batch Modal */}
      {showBatchModal && (
        <BatchModal
          onClose={() => setShowBatchModal(false)}
          onRun={runBatchClassify}
          loading={batchLoading}
          results={batchResults}
          selectedCount={selectedTransactions.length}
          totalCount={transactions.length}
        />
      )}

      {/* Attribution Modal */}
      {showAttributionModal && (
        <AttributionModal
          transaction={attributionTx}
          matches={clientMatches}
          loading={attributionLoading}
          onClose={() => {
            setShowAttributionModal(false);
            setAttributionTx(null);
            setClientMatches([]);
          }}
          onConfirm={confirmAttribution}
          formatAmount={formatAmount}
        />
      )}

      {/* Batch Attribution Modal */}
      {showBatchAttributionModal && (
        <BatchAttributionModal
          config={batchConfig}
          results={batchAttributionResults}
          loading={batchAttributionLoading}
          selectedCount={selectedTransactions.length}
          onClose={() => {
            setShowBatchAttributionModal(false);
            setBatchAttributionResults(null);
          }}
          onDryRun={() => runBatchAttribution(true)}
          onExecute={() => runBatchAttribution(false)}
        />
      )}

      {/* Pennylane Link Modal */}
      {showPennylaneLinkModal && pennylaneLinkTransaction && (
        <PennylaneLinkModal
          transaction={pennylaneLinkTransaction}
          onClose={() => {
            setShowPennylaneLinkModal(false);
            setPennylaneLinkTransaction(null);
          }}
          onSuccess={(result) => {
            console.log('Pennylane link success:', result);
            // Optionally reload data to reflect changes
            loadData();
          }}
          formatAmount={formatAmount}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════

function StatCard({ icon, label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    orange: 'bg-orange-50 text-orange-600 border-orange-200',
  };

  return (
    <div className={`p-4 rounded-xl border ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-sm font-medium opacity-80">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

function TransactionsTab({ 
  transactions, 
  onClassify, 
  classifyingId,
  selectedTransactions,
  setSelectedTransactions,
  getClassificationBadge,
  formatAmount,
  onFindClient,
  getAttributionStatus,
  onPennylaneLink
}) {
  const toggleSelection = (id) => {
    setSelectedTransactions(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  return (
    <div className="bg-white rounded-xl border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedTransactions.length === transactions.length && transactions.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedTransactions(transactions.map(t => t.id));
                    } else {
                      setSelectedTransactions([]);
                    }
                  }}
                  className="rounded"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Fournisseur</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Libellé</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Montant</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Classification v2</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Attribution v2</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {transactions.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                  Aucune transaction trouvée
                </td>
              </tr>
            ) : (
              transactions.map((tx) => {
                const attribution = getAttributionStatus(tx.id) || tx._v2_attribution;
                return (
                  <tr key={tx.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedTransactions.includes(tx.id)}
                        onChange={() => toggleSelection(tx.id)}
                        className="rounded"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{tx.date}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {tx.supplier || tx.supplier_detected || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                      {tx.label || tx.full_line}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium">
                      <span className={tx.amount < 0 ? 'text-red-600' : 'text-green-600'}>
                        {formatAmount(tx.amount)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {tx._v2_classification ? (
                        getClassificationBadge(tx._v2_classification)
                      ) : (
                        <span className="text-xs text-gray-400">Non classifié</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {attribution ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                          attribution.status === 'confirmed' 
                            ? 'bg-green-100 text-green-700'
                            : attribution.status === 'suggested'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          <UserCheck className="w-3 h-3" />
                          {attribution.best_client_name || attribution.client?.nom || 'Client'}
                          <span className="opacity-60">({Math.round(attribution.probability)}%)</span>
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => onClassify(tx)}
                          disabled={classifyingId === tx.id}
                          className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 disabled:opacity-50"
                          title="Classifier"
                        >
                          {classifyingId === tx.id ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            <Brain className="w-3 h-3" />
                          )}
                        </button>
                        <button
                          onClick={() => onFindClient(tx)}
                          className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                          title="Trouver client"
                          data-testid={`find-client-${tx.id}`}
                        >
                          <Users className="w-3 h-3" />
                        </button>
                        {onPennylaneLink && (
                          <button
                            onClick={() => onPennylaneLink(tx)}
                            className="px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200"
                            title="Rattacher à Pennylane"
                            data-testid={`pennylane-link-${tx.id}`}
                          >
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LearningsTab({ learnings, formatAmount }) {
  return (
    <div className="bg-white rounded-xl border overflow-hidden">
      <div className="p-4 border-b bg-gray-50">
        <h3 className="font-semibold text-gray-900">Apprentissages Rachel v2</h3>
        <p className="text-sm text-gray-500">Fournisseurs et clients appris pour ce tenant</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Nom original</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Catégorie</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Source</th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Confiance</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {learnings.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  Aucun apprentissage pour ce tenant
                </td>
              </tr>
            ) : (
              learnings.map((learning, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs rounded ${
                      learning.kind === 'supplier' ? 'bg-blue-100 text-blue-700' :
                      learning.kind === 'customer' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {learning.kind}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900">{learning.original_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{learning.mapping?.categorie || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs rounded ${
                      learning.source === 'manual' ? 'bg-purple-100 text-purple-700' :
                      learning.source === 'ai_auto_learn' ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {learning.source}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm font-medium">{Math.round(learning.confidence * 100)}%</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(learning.created_at).toLocaleDateString('fr-FR')}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DecisionsTab({ decisions, getClassificationBadge }) {
  return (
    <div className="bg-white rounded-xl border overflow-hidden">
      <div className="p-4 border-b bg-gray-50">
        <h3 className="font-semibold text-gray-900">Décisions Rachel v2</h3>
        <p className="text-sm text-gray-500">Historique des classifications et attributions</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Transaction</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Classification</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {decisions.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                  Aucune décision enregistrée
                </td>
              </tr>
            ) : (
              decisions.map((decision, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="text-sm font-mono text-gray-600">
                      {decision.transaction_ref?.source}:{decision.transaction_ref?.id}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {getClassificationBadge(decision.classification)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs rounded ${
                      decision.status === 'classified' ? 'bg-green-100 text-green-700' :
                      decision.status === 'attributed' ? 'bg-purple-100 text-purple-700' :
                      decision.status === 'review_required' ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {decision.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(decision.created_at).toLocaleString('fr-FR')}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BatchModal({ onClose, onRun, loading, results, selectedCount, totalCount }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold">Analyse en Masse (v2)</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircle className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {!results ? (
            <div className="text-center py-8">
              <Brain className="w-12 h-12 text-purple-300 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">
                {selectedCount > 0 
                  ? `${selectedCount} transactions sélectionnées`
                  : `${Math.min(50, totalCount)} transactions (max 50)`
                }
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Rachel v2 va classifier ces transactions avec l'IA et les règles métier.
              </p>
              <button
                onClick={onRun}
                disabled={loading}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 mx-auto"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Analyse en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Lancer l'analyse
                  </>
                )}
              </button>
            </div>
          ) : (
            <div>
              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{results.stats.total}</p>
                  <p className="text-xs text-blue-600">Total analysé</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{results.stats.classified}</p>
                  <p className="text-xs text-green-600">Classifiés</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-600">{results.stats.attributable}</p>
                  <p className="text-xs text-purple-600">Attribuables</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-gray-600">{results.stats.general_charges}</p>
                  <p className="text-xs text-gray-600">Charges générales</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-orange-600">{results.stats.ai_consulted}</p>
                  <p className="text-xs text-orange-600">IA consultée</p>
                </div>
                <div className="bg-pink-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-pink-600">{results.stats.auto_learned}</p>
                  <p className="text-xs text-pink-600">Auto-appris</p>
                </div>
              </div>
              
              {/* Results Preview */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b">
                  <h4 className="font-medium text-sm text-gray-700">Résultats ({results.results.length})</h4>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {results.results.slice(0, 10).map((r, idx) => (
                    <div key={idx} className="px-4 py-2 border-b last:border-b-0 flex items-center justify-between">
                      <span className="text-sm font-medium">{r.supplier || r.transaction_id}</span>
                      <span className={`px-2 py-0.5 text-xs rounded ${
                        r.classification?.est_attribuable_chantier 
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {r.classification?.categorie || 'N/A'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

function AttributionModal({ transaction, matches, loading, onClose, onConfirm, formatAmount }) {
  const getConfidenceColor = (level) => {
    switch (level) {
      case 'high': return 'bg-green-100 text-green-700 border-green-200';
      case 'medium': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'low': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden" data-testid="attribution-modal">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Trouver Client (v2)</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        {/* Transaction Info */}
        {transaction && (
          <div className="p-4 bg-gray-50 border-b">
            <p className="text-sm text-gray-500 mb-1">Transaction à attribuer</p>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">{transaction.supplier || transaction.supplier_detected || 'Fournisseur inconnu'}</p>
                <p className="text-sm text-gray-600 truncate max-w-md">{transaction.label || transaction.full_line}</p>
              </div>
              <div className="text-right">
                <p className="font-bold text-lg">{formatAmount(transaction.amount)}</p>
                <p className="text-xs text-gray-500">{transaction.date}</p>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[50vh]">
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-3" />
              <p className="text-gray-600">Recherche des clients correspondants...</p>
            </div>
          ) : matches.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">Aucun client correspondant trouvé</p>
              <p className="text-sm text-gray-400 mt-1">Aucun client actif ne correspond aux critères de la transaction</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 mb-3">
                {matches.length} client{matches.length > 1 ? 's' : ''} trouvé{matches.length > 1 ? 's' : ''} (top 5)
              </p>
              
              {matches.map((match, idx) => (
                <div 
                  key={idx} 
                  className={`p-4 rounded-xl border-2 transition ${getConfidenceColor(match.confidence_level)} hover:shadow-md`}
                  data-testid={`client-match-${idx}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Client Header */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-sm font-bold ${
                          match.probability >= 70 ? 'bg-green-500 text-white' :
                          match.probability >= 45 ? 'bg-orange-500 text-white' :
                          'bg-gray-400 text-white'
                        }`}>
                          {Math.round(match.probability)}%
                        </span>
                        <h3 className="font-semibold text-gray-900">{match.client.nom}</h3>
                        {idx === 0 && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                            Meilleur match
                          </span>
                        )}
                      </div>
                      
                      {/* Client Details */}
                      <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                        <div className="flex items-center gap-1 text-gray-600">
                          <MapPin className="w-3 h-3" />
                          {match.client.ville || 'Ville non renseignée'}
                          {match.client.code_postal && ` (${match.client.code_postal})`}
                        </div>
                        <div className="text-gray-600">
                          Statut: <span className="font-medium">{match.client.statut || match.client.etape || '-'}</span>
                        </div>
                      </div>
                      
                      {/* Reasons */}
                      {match.reasons && match.reasons.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs text-gray-500 font-medium">Raisons du match:</p>
                          <div className="flex flex-wrap gap-1">
                            {match.reasons.slice(0, 4).map((reason, rIdx) => (
                              <span key={rIdx} className="px-2 py-0.5 bg-white/50 rounded text-xs text-gray-700">
                                {reason.detail || reason.type}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Confirm Button */}
                    <button
                      onClick={() => onConfirm(match)}
                      disabled={loading}
                      className="ml-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition disabled:opacity-50"
                      data-testid={`confirm-attribution-${idx}`}
                    >
                      <ThumbsUp className="w-4 h-4" />
                      Confirmer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
          <p className="text-xs text-gray-500">
            ⚠️ Attribution v2 - N'écrit PAS dans les collections v1
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

function BatchAttributionModal({ config, results, loading, selectedCount, onClose, onDryRun, onExecute }) {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'suggested': return <Eye className="w-4 h-4 text-orange-500" />;
      case 'confirmed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'skipped': return <XCircle className="w-4 h-4 text-gray-400" />;
      case 'no_match': return <Users className="w-4 h-4 text-red-400" />;
      case 'already_attributed': return <UserCheck className="w-4 h-4 text-blue-400" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'suggested': return 'Suggéré';
      case 'confirmed': return 'Confirmé';
      case 'skipped': return 'Ignoré';
      case 'no_match': return 'Pas de match';
      case 'already_attributed': return 'Déjà attribué';
      default: return status;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] overflow-hidden" data-testid="batch-attribution-modal">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Attribution Batch v2</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        {/* Config Info */}
        <div className="p-4 bg-gray-50 border-b">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Configuration</h3>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Auto-confirm</p>
              <p className={`font-medium ${config?.auto_confirm_enabled ? 'text-green-600' : 'text-gray-600'}`}>
                {config?.auto_confirm_enabled ? '✅ Activé' : '🔒 Désactivé'}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Seuil auto-confirm</p>
              <p className="font-medium">{Math.round((config?.auto_confirm_threshold || 0.7) * 100)}%</p>
            </div>
            <div>
              <p className="text-gray-500">Seuil suggestion</p>
              <p className="font-medium">{Math.round((config?.suggestion_threshold || 0.3) * 100)}%</p>
            </div>
            <div>
              <p className="text-gray-500">Max par run</p>
              <p className="font-medium">{config?.max_per_run || 20}</p>
            </div>
          </div>
          {selectedCount > 0 && (
            <p className="mt-2 text-sm text-blue-600">
              📋 {selectedCount} transaction(s) sélectionnée(s)
            </p>
          )}
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[50vh]">
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-3" />
              <p className="text-gray-600">Recherche de clients en cours...</p>
            </div>
          ) : results ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-5 gap-3">
                <div className="bg-gray-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-700">{results.summary?.total || 0}</p>
                  <p className="text-xs text-gray-500">Total</p>
                </div>
                <div className="bg-orange-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-orange-600">{results.summary?.suggested || 0}</p>
                  <p className="text-xs text-orange-600">Suggérés</p>
                </div>
                <div className="bg-green-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{results.summary?.confirmed || 0}</p>
                  <p className="text-xs text-green-600">Confirmés</p>
                </div>
                <div className="bg-gray-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-500">{results.summary?.skipped || 0}</p>
                  <p className="text-xs text-gray-500">Ignorés</p>
                </div>
                <div className="bg-blue-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-600">{results.summary?.already_attributed || 0}</p>
                  <p className="text-xs text-blue-600">Déjà attribués</p>
                </div>
              </div>

              {/* Dry run notice */}
              {results.dry_run && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-yellow-600" />
                  <p className="text-yellow-800 text-sm">
                    <strong>Mode simulation (Dry Run)</strong> — Aucune donnée n'a été enregistrée. 
                    Cliquez sur "Exécuter" pour sauvegarder les suggestions.
                  </p>
                </div>
              )}

              {/* Results list */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Statut</th>
                      <th className="px-3 py-2 text-left">Transaction</th>
                      <th className="px-3 py-2 text-left">Client suggéré</th>
                      <th className="px-3 py-2 text-right">Score</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {results.results?.map((r, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <span className="inline-flex items-center gap-1">
                            {getStatusIcon(r.status)}
                            <span className={`text-xs ${
                              r.status === 'confirmed' ? 'text-green-600' :
                              r.status === 'suggested' ? 'text-orange-600' :
                              'text-gray-500'
                            }`}>
                              {getStatusLabel(r.status)}
                            </span>
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <p className="font-medium text-gray-800">{r.supplier || 'Fournisseur inconnu'}</p>
                          <p className="text-xs text-gray-500 truncate max-w-xs">{r.transaction_id?.substring(0, 20)}...</p>
                        </td>
                        <td className="px-3 py-2">
                          {r.best_match ? (
                            <p className="text-gray-700">{r.best_match.client_name}</p>
                          ) : (
                            <span className="text-gray-400">{r.reason || r.existing_client || '—'}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {r.best_match && (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              r.best_match.probability >= 70 ? 'bg-green-100 text-green-700' :
                              r.best_match.probability >= 45 ? 'bg-orange-100 text-orange-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {Math.round(r.best_match.probability)}%
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <Zap className="w-12 h-12 text-blue-200 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">Attribution Batch</p>
              <p className="text-sm text-gray-400 mt-1 max-w-md mx-auto">
                Trouvez automatiquement les clients correspondants pour plusieurs transactions.
                Commencez par un "Dry Run" pour prévisualiser les résultats avant de les enregistrer.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
          <p className="text-xs text-gray-500">
            {config?.auto_confirm_enabled 
              ? `⚠️ Auto-confirm actif pour scores ≥ ${Math.round(config.auto_confirm_threshold * 100)}%`
              : '🔒 Mode suggestion uniquement (auto-confirm désactivé)'}
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Fermer
            </button>
            <button
              onClick={onDryRun}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition disabled:opacity-50"
              data-testid="batch-dry-run"
            >
              <Eye className="w-4 h-4" />
              Dry Run
            </button>
            {results && results.dry_run && (results.summary?.suggested > 0 || results.summary?.confirmed > 0) && (
              <button
                onClick={onExecute}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition disabled:opacity-50"
                data-testid="batch-execute"
              >
                <PlayCircle className="w-4 h-4" />
                Exécuter
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
